import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useFollow } from '../contexts/FollowContext.tsx';
import { fetchSubSubCategories, fetchItems, fetchSubCategories, fetchMainCategories } from '../services/googleSheets.ts';
import { SubSubCategory, Item } from '../types';
import LoadingOverlay from './LoadingOverlay.tsx';
import ImageWithFallback from './ImageWithFallback.tsx';
import SEOHead from './SEOHead.tsx';
import './SubCategoryPage.css';
import './shared/SearchAndBackStyles.css';
import LoginModal from './auth/LoginModal.tsx';

const SubCategoryPage: React.FC = () => {
    const { url } = useParams();
    const location = useLocation();
    const { id, mainCategoryId } = location.state || {};
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    const { followedItems, followItem, unfollowItem, isFollowing, refreshFollowedItems } = useFollow();
    const [subSubCategories, setSubSubCategories] = useState<SubSubCategory[]>([]);
    const [items, setItems] = useState<Item[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('all');
    const [currentSubCategory, setCurrentSubCategory] = useState<any>(null);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                if (!url) {
                    navigate('/');
                    return;
                }

                let matchingSubCategory;
                let fetchedItems;

                if (!id || !mainCategoryId) {
                    const allMainCategories = await fetchMainCategories();
                    for (const mainCategory of allMainCategories) {
                        const subCategories = await fetchSubCategories(mainCategory.id);
                        matchingSubCategory = subCategories.find(sub => sub.url === url);
                        if (matchingSubCategory) {
                            const [subSubCatsData, itemsData] = await Promise.all([
                                fetchSubSubCategories(matchingSubCategory.id),
                                fetchItems('all') // Initially fetch all items, we'll filter in the component
                            ]);
                            setSubSubCategories(subSubCatsData);
                            fetchedItems = itemsData.filter(item => 
                                item.subSubCategoryId && subSubCatsData.some(sub => sub.id === item.subSubCategoryId)
                            );
                            setCurrentSubCategory(matchingSubCategory);
                            break;
                        }
                    }
                } else {
                    const [allSubCategories, subSubCatsData, itemsData] = await Promise.all([
                        fetchSubCategories(mainCategoryId),
                        fetchSubSubCategories(id),
                        fetchItems('all') // Initially fetch all items, we'll filter in the component
                    ]);
                    matchingSubCategory = allSubCategories.find(sub => sub.id === id);
                    setCurrentSubCategory(matchingSubCategory);
                    setSubSubCategories(subSubCatsData);
                    fetchedItems = itemsData.filter(item => 
                        item.subSubCategoryId && subSubCatsData.some(sub => sub.id === item.subSubCategoryId)
                    );
                }

                // Sort items: followed first, then alphabetically
                if (fetchedItems) {
                    const sortedItems = sortItems(fetchedItems, followedItems);
                    setItems(sortedItems);
                }
            } catch (error) {
                console.error('Error loading data:', error);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [url, id, mainCategoryId, navigate, followedItems]);

    // Add a new effect to reload items when the active tab changes
    useEffect(() => {
        const reloadItemsForTab = async () => {
            if (!activeTab || !subSubCategories.length) return;
            
            setLoading(true);
            try {
                let fetchedItems;
                
                if (activeTab === 'all') {
                    // For "all" tab, fetch all items for all subcategories
                    fetchedItems = await fetchItems('all');
                    // Filter to only include items that belong to our subcategories
                    fetchedItems = fetchedItems.filter(item => 
                        item.subSubCategoryId && subSubCategories.some(sub => sub.id === item.subSubCategoryId)
                    );
                } else {
                    // For specific tab, fetch only items for that subcategory
                    fetchedItems = await fetchItems(activeTab);
                }
                
                const sortedItems = sortItems(fetchedItems, followedItems);
                setItems(sortedItems);
            } catch (error) {
                console.error('Error loading items for tab:', error);
            } finally {
                setLoading(false);
            }
        };
        
        reloadItemsForTab();
    }, [activeTab, subSubCategories, followedItems]);

    const sortItems = (items: Item[], followedItems: string[]) => {
        const followedSet = new Set(followedItems);
        
        // Separate followed and unfollowed items
        const followed = items.filter(item => followedSet.has(item.id));
        const unfollowed = items.filter(item => !followedSet.has(item.id));
        
        // Sort followed items alphabetically by title
        const sortedFollowed = followed.sort((a, b) => 
            a.title.localeCompare(b.title, 'ar')
        );
        
        // Keep unfollowed items in their original order
        return [...sortedFollowed, ...unfollowed];
    };

    const filteredItems = items.filter(item => {
        const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesSearch;
    });

    const handleFollow = async (itemId: string) => {
        if (!isAuthenticated) {
            setIsLoginModalOpen(true);
            return;
        }

        try {
            //console.log('Handling follow for item:', itemId);
            if (isFollowing(itemId)) {
                await unfollowItem(itemId);
                //console.log('Successfully unfollowed item:', itemId);
            } else {
                await followItem(itemId);
                //console.log('Successfully followed item:', itemId);
            }

            // Sort items after follow/unfollow
            const updatedItems = [...items];
            const sortedItems = sortItems(updatedItems, followedItems);
            setItems(sortedItems);
        } catch (error) {
            console.error('Error in follow/unfollow:', error);
            alert('حدث خطأ أثناء تحديث المتابعة');
        }
    };

    const handleExportFollows = () => {
        if (!user) {
            setIsLoginModalOpen(true);
            return;
        }
        window.location.href = `https://taj.city/backend/api/follow/export_follows.php?user_id=${user.id}`;
    };

    const handleImportFollows = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!user) {
            setIsLoginModalOpen(true);
            return;
        }

        const file = event.target.files?.[0];
        if (!file) {
            alert('الرجاء اختيار ملف');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('user_id', user.id.toString());

        try {
            const response = await fetch('https://taj.city/backend/api/follow/import_follows.php', {
                method: 'POST',
                body: formData,
                headers: {
                    'Accept': 'application/json'
                }
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'فشل في استيراد المتابعين');
            }

            alert(data.message);
            await refreshFollowedItems();
            
            // Clear the file input
            event.target.value = '';
        } catch (error) {
            console.error('Error importing follows:', error);
            alert('فشل في استيراد المتابعين: ' + (error instanceof Error ? error.message : 'خطأ غير معروف'));
        }
    };

    if (loading) return <LoadingOverlay />;
    return (
        <>
            {currentSubCategory && (
                <SEOHead
                    title={currentSubCategory.title || 'وصال سيتي'}
                    description={currentSubCategory.description || `تصفح ${currentSubCategory.title} في وصال سيتي`}
                    keywords={currentSubCategory.Meta_key_Words || `وصال سيتي, ${currentSubCategory.title}`}
                    url={window.location.href}
                    type="website"
                />
            )}
            <div className="subcategory-page">
                <div className="back-button-container">
                    <button onClick={() => navigate(-1)} className="back-button">
                        <svg viewBox="0 0 24 24">
                            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                        </svg>
                        <span>رجوع</span>
                    </button>
                    <button onClick={() => navigate('/')} className="home-button">
                        <svg viewBox="0 0 24 24">
                            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                        </svg>
                        <span>القائمة الرئيسية</span>
                    </button>
                    {isAuthenticated && (
                        <div className="follows-actions">
                            <button onClick={handleExportFollows} className="export-button">
                                <svg viewBox="0 0 24 24">
                                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                                </svg>
                                <span>تصدير المتابعين</span>
                            </button>
                            <label className="import-button">
                                <svg viewBox="0 0 24 24">
                                    <path d="M5 20h14v-2H5v2zm0-10h4v6h6v-6h4l-7-7-7 7z"/>
                                </svg>
                                <span>استيراد المتابعين</span>
                                <input
                                    type="file"
                                    accept=".csv"
                                    onChange={handleImportFollows}
                                    style={{ display: 'none' }}
                                />
                            </label>
                        </div>
                    )}
                </div>

                <div className="search-container">
                    <div className="search-bar">
                        <svg className="search-icon" viewBox="0 0 24 24">
                            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                        </svg>
                        <input
                            type="text"
                            placeholder="ابحث..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                        />
                        {searchQuery && (
                            <button
                                className="clear-search"
                                onClick={() => setSearchQuery('')}
                            >
                                <span>×</span>
                            </button>
                        )}
                    </div>
                </div>

                <div className="tabs-container">
                    <button
                        className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
                        onClick={() => setActiveTab('all')}
                    >
                        الكل
                    </button>
                    {subSubCategories.map(category => (
                        <button
                            key={category.id}
                            className={`tab-button ${activeTab === category.id ? 'active' : ''}`}
                            onClick={() => {
                                setActiveTab(category.id);
                                // Scroll back to top when changing tabs
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                        >
                            {category.title}
                        </button>
                    ))}
                </div>

                <h1 className="subcategory-title">
                    {currentSubCategory?.title}
                    {activeTab !== 'all' && (
                        <span className="subcategory-subtitle">
                            - {subSubCategories.find(c => c.id === activeTab)?.title}
                        </span>
                    )}
                </h1>

                {loading ? (
                    <LoadingOverlay />
                ) : filteredItems.length > 0 ? (
                    <div className="items-grid">
                        {filteredItems.map(item => (
                            <ItemCard
                                key={item.id}
                                item={item}
                                isFollowing={isFollowing(item.id)}
                                onFollow={() => handleFollow(item.id)}
                                subcategoryUrl={url || ''}
                                onClick={() => navigate(`/${url}/${item.url}`, { state: { id: item.id } })}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="no-results">
                        <div className="no-results-icon">
                            <svg viewBox="0 0 24 24">
                                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                            </svg>
                        </div>
                        <p>لا توجد نتائج مطابقة للبحث</p>
                    </div>
                )}
            </div>
            <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
        </>
    );
};

const ItemCard: React.FC<{
    item: Item;
    isFollowing: boolean;
    onFollow: () => void;
    subcategoryUrl: string;
    onClick: () => void;
}> = ({ item, isFollowing, onFollow, subcategoryUrl, onClick }) => {
    const handleShare = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (navigator.share) {
            try {
                const shareUrl = `${window.location.origin}/${subcategoryUrl}/${item.url}`;
                await navigator.share({
                    title: item.title,
                    text: item.description,
                    url: shareUrl
                });
            } catch (error) {
                console.error('Error sharing:', error);
            }
        }
    };

    const handleFollow = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onFollow();
    };

    const firstPhone = item.phone?.split(',')[0];
    const firstWhatsapp = item.whatsapp?.split(',')[0];

    return (
        <div className="item-card" onClick={onClick}>
            {item.images.length > 0 && (
                <div className="item-image-container">
                    <ImageWithFallback
                        src={item.images[0]}
                        alt={item.title}
                        className="item-image"
                        hideOnError={true}
                    />
                </div>
            )}
            <div className="item-content">
                <div className="item-header">
                    <h3>{item.title}</h3>
                    <button className="share-button" onClick={handleShare}>
                        <svg viewBox="0 0 24 24">
                            <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92z" />
                        </svg>
                    </button>
                </div>
                <p className="item-description">{item.description}</p>
                <div className="contact-section">
                    <div className="contact-icons">
                        {firstPhone && (
                            <a href={`tel:${firstPhone}`} className="contact-icon phone" onClick={e => e.stopPropagation()}>
                                <svg viewBox="0 0 24 24">
                                    <path fill="#fff" d="M6.62 10.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                                </svg>
                            </a>
                        )}
                        {firstWhatsapp && (
                            <a href={`https://wa.me/${firstWhatsapp}`} target="_blank" rel="noopener noreferrer" className="contact-icon whatsapp" onClick={e => e.stopPropagation()}>
                                <svg viewBox="0 0 24 24">
                                    <path fill="#fff" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                </svg>
                            </a>
                        )}
                        {item.website && (
                            <a href={item.website} target="_blank" rel="noopener noreferrer" className="contact-icon website" onClick={e => e.stopPropagation()}>
                                <svg viewBox="0 0 24 24">
                                    <path fill="#fff" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                                </svg>
                            </a>
                        )}
                        {item.location && (
                            <a href={item.location} target="_blank" rel="noopener noreferrer" className="contact-icon location" onClick={e => e.stopPropagation()}>
                                <svg viewBox="0 0 24 24">
                                    <path fill="#fff" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zM7 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 2.88-2.88 7.19-5 9.88C9.92 16.21 7 11.85 7 9z" />
                                </svg>
                            </a>
                        )}
                        {item.facebook && (
                            <a href={item.facebook} target="_blank" rel="noopener noreferrer" className="contact-icon facebook" onClick={e => e.stopPropagation()}>
                                <svg viewBox="0 0 24 24">
                                    <path fill="#fff" d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z" />
                                </svg>
                            </a>
                        )}
                        {item.delivery && (
                            <div className="contact-icon delivery-badge" title="خدمة التوصيل متوفرة">
                                <svg viewBox="0 0 24 24">
                                    <path fill="#fff" d="M19 7c0-1.1-.9-2-2-2h-3v2h3v2.65L13.52 14H10V9H6c-2.21 0-4 1.79-4 4v3h2c0 1.66 1.34 3 3 3s3-1.34 3-3h4.48L19 10.35V7zM7 17c-.55 0-1-.45-1-1h2c0 .55-.45 1-1 1z" />
                                </svg>
                            </div>
                        )}
                        {item.isOpen && (
                            <div className="contact-icon open-badge" title="مفتوح 24/7">
                                <svg viewBox="0 0 24 24">
                                    <path fill="#fff" d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
                                    <path fill="#fff" d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                                </svg>
                            </div>
                        )}
                    </div>
                </div>
                <div className="item-actions">
                    <button className="details-button">عرض التفاصيل</button>
                    <button
                        className={`follows-button ${isFollowing ? 'following' : ''}`}
                        onClick={handleFollow}
                    >
                        {isFollowing ? 'إلغاء المتابعة' : 'متابعة'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SubCategoryPage; 